/* eslint-disable @typescript-eslint/camelcase */
import { ISandbox, ISandboxImage } from 'holberton-school-intranet-api';
import { groupBy, map, orderBy, random } from 'lodash';
import * as React from 'react';
import { ReactElement, useEffect, useRef, useState } from 'react';

import { del, get, post } from '../../api/utils';

import Sandbox from './Sandbox';

interface SandboxesPageSectionProps {
    accent: 'containers' | 'ssh' | 'vpn';
    iconClass: string;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}

function SandboxesPageSection(props: SandboxesPageSectionProps): ReactElement {
    const { accent, children, iconClass, subtitle, title } = props;
    const titleId = `sandboxes-section-heading-${accent}`;

    return (
        <section
            aria-labelledby={titleId}
            className={`sandboxes-section sandboxes-section--${accent}`}
        >
            <header className="sandboxes-section__head">
                <span className="sandboxes-section__icon-wrap">
                    <i aria-hidden className={`fa ${iconClass} sandboxes-section__icon`} />
                </span>
                <div className="sandboxes-section__head-text">
                    <h2 className="sandboxes-section__title" id={titleId}>
                        {title}
                    </h2>
                    {subtitle ? (
                        <p className="sandboxes-section__subtitle">{subtitle}</p>
                    ) : null}
                </div>
            </header>
            <div className="sandboxes-section__body">{children}</div>
        </section>
    );
}

export interface ISshKey {
    id: number | string;
    title: string;
    key: string;
}

export interface IVpnRegion {
    aws_region_id: number | string;
    aws_region_full_name: string;
}

export interface IVpnConfig {
    aws_region_id: number | string;
    aws_region_full_name: string;
    creation_date?: string;
    expiration_date?: string;
}

export interface IProps {
    images: ISandboxImage[];
    existingSandboxes?: ISandbox[] | undefined;
    sandboxesUri: string;
    sshKeysUri?: string;
    vpnConfigsUri?: string;
    csrfToken: string;
    maxNumberOfContainers: number;
    standalone?: boolean;
    modalId?: string;
}

export default function Sandboxes({
    images,
    existingSandboxes = undefined,
    sandboxesUri,
    sshKeysUri,
    vpnConfigsUri,
    csrfToken,
    maxNumberOfContainers,
    standalone = false,
    modalId = 'sandboxes-modal',
}: IProps): ReactElement {
    const [sandboxes, setSandboxes] = useState<ISandbox[] | undefined>(
        existingSandboxes,
    );
    const [loading, setLoading] = useState<boolean>(existingSandboxes === undefined);
    const [imageSearch, setImageSearch] = useState<string>('');
    const [newSandboxOpen, setNewSandboxOpen] = useState<boolean>(false);
    const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
    const [sshKeys, setSshKeys] = useState<ISshKey[] | undefined>(undefined);
    const [sshKeysLoading, setSshKeysLoading] = useState<boolean>(false);
    const [sshKeysAddOpen, setSshKeysAddOpen] = useState<boolean>(false);
    const [sshKeyTitle, setSshKeyTitle] = useState<string>('');
    const [sshKeyValue, setSshKeyValue] = useState<string>('');
    const [sshKeySubmitting, setSshKeySubmitting] = useState<boolean>(false);
    const [vpnRegions, setVpnRegions] = useState<IVpnRegion[]>([]);
    const [vpnConfigs, setVpnConfigs] = useState<IVpnConfig[]>([]);
    const [vpnLoading, setVpnLoading] = useState<boolean>(false);
    const [vpnAddOpen, setVpnAddOpen] = useState<boolean>(false);
    const [vpnSelectedRegionId, setVpnSelectedRegionId] = useState<string>('');
    const [vpnSubmitting, setVpnSubmitting] = useState<boolean>(false);

    const toggleRegion = (regionName: string): void => {
        setExpandedRegions((prev) => {
            const next = new Set(prev);
            if (next.has(regionName)) {
                next.delete(regionName);
            } else {
                next.add(regionName);
            }
            return next;
        });
    };
    const hasFetchedSandboxesRef = useRef<boolean>(false);
    const hasFetchedSshKeysRef = useRef<boolean>(false);
    const hasFetchedVpnRef = useRef<boolean>(false);
    const imagesByRegion: {
        key: string;
        values: ISandboxImage[];
    } = groupBy(orderBy(images, 'name'), 'aws_region');

    const filteredImagesByRegion = imageSearch.trim()
        ? Object.fromEntries(
            Object.entries(imagesByRegion).map(([region, imgs]) => [
                region,
                imgs.filter((img) =>
                    img.name.toLowerCase().includes(imageSearch.trim().toLowerCase()),
                ),
            ]).filter(([, imgs]) => imgs.length > 0),
        )
        : imagesByRegion;

    const fetchSandboxes = async (): Promise<void> => {
        if (hasFetchedSandboxesRef.current) {
            return;
        }
        hasFetchedSandboxesRef.current = true;

        try {
            const {
                sandboxes: fetchedSandboxes = [],
                success,
                data,
            }: { success: boolean; sandboxes?: ISandbox[]; data?: string } = await get(
                `${sandboxesUri}.json`,
                csrfToken,
                {},
            );

            if (success === false) {
                alert(data || 'Failed to fetch sandboxes');
                setLoading(false);
                hasFetchedSandboxesRef.current = false;
                return;
            }

            setSandboxes(fetchedSandboxes);
            setLoading(false);
        } catch (err) {
            console.error(err);
            alert(window.I18n.t('sandboxes.error_fetching', 'Error while fetching sandboxes. Try again later.'));
            setLoading(false);
            hasFetchedSandboxesRef.current = false;
        }
    };

    const fetchSshKeys = async (): Promise<void> => {
        if (!sshKeysUri || hasFetchedSshKeysRef.current) {
            return;
        }
        hasFetchedSshKeysRef.current = true;
        setSshKeysLoading(true);
        try {
            const res: { success?: boolean; data?: ISshKey[]; keys?: ISshKey[] } = await get(
                `${sshKeysUri}.json`,
                csrfToken,
                {},
            );
            const keys = Array.isArray(res) ? res : (res.data || res.keys || []);
            setSshKeys(Array.isArray(keys) ? keys : []);
        } catch (err) {
            setSshKeys([]);
            hasFetchedSshKeysRef.current = false;
        } finally {
            setSshKeysLoading(false);
        }
    };

    const fetchVpn = async (): Promise<void> => {
        if (!vpnConfigsUri || hasFetchedVpnRef.current) {
            return;
        }
        hasFetchedVpnRef.current = true;
        setVpnLoading(true);
        try {
            const res: {
                success?: boolean;
                data?: { configs?: IVpnConfig[]; regions?: IVpnRegion[] };
            } = await get(`${vpnConfigsUri}.json`, csrfToken, {});
            const data = res.data;
            const regions = Array.isArray(data?.regions) ? data.regions : [];
            const configs = Array.isArray(data?.configs) ? data.configs : [];
            setVpnRegions(regions);
            setVpnConfigs(configs);
        } catch (_err) {
            setVpnRegions([]);
            setVpnConfigs([]);
            hasFetchedVpnRef.current = false;
        } finally {
            setVpnLoading(false);
        }
    };

    const loadStandaloneExtras = (): void => {
        if (!standalone) {
            return;
        }
        void fetchSshKeys();
        void fetchVpn();
    };

    const loadModalData = (): void => {
        void fetchSandboxes();
        loadStandaloneExtras();
    };

    useEffect((): void => {
        const modalElement = document.getElementById(modalId);
        const inModal = modalElement != null;

        if (existingSandboxes !== undefined) {
            if (!inModal) {
                loadStandaloneExtras();
            }
            return;
        }

        if (!inModal) {
            loadModalData();
            return;
        }

        const handleModalShown = (): void => {
            loadModalData();
        };

        if (typeof window.jQuery !== 'undefined') {
            window.jQuery(modalElement).on('shown.bs.modal', handleModalShown);
        } else {
            modalElement.addEventListener('shown.bs.modal', handleModalShown);
        }

        if (modalElement.classList.contains('show') || modalElement.classList.contains('in')) {
            loadModalData();
        }

        return (): void => {
            if (typeof window.jQuery !== 'undefined') {
                window.jQuery(modalElement).off('shown.bs.modal', handleModalShown);
            } else {
                modalElement.removeEventListener('shown.bs.modal', handleModalShown);
            }
        };
    }, [existingSandboxes, sandboxesUri, csrfToken, modalId, standalone, sshKeysUri, vpnConfigsUri]);

    const vpnRegionsWithoutConfig = vpnRegions.filter(
        (r) => !(vpnConfigs || []).some((c) => String(c.aws_region_id) === String(r.aws_region_id)),
    );

    const addVpn = async (): Promise<void> => {
        if (!vpnConfigsUri || !vpnSelectedRegionId) return;
        setVpnSubmitting(true);
        try {
            const res: { success?: boolean; data?: IVpnConfig; error?: string } = await post(
                `${vpnConfigsUri}.json`,
                csrfToken,
                { aws_region_id: Number(vpnSelectedRegionId) },
            );
            if (res.success) {
                setVpnSelectedRegionId('');
                setVpnAddOpen(false);
                await fetchVpn();
            } else {
                alert(
                    res.error ||
                        window.I18n.t('sandboxes.vpn_create_error', 'Failed to create VPN configuration'),
                );
            }
        } catch (err) {
            alert(
                (err as Error).message ||
                    window.I18n.t('sandboxes.vpn_create_error', 'Failed to create VPN configuration'),
            );
        } finally {
            setVpnSubmitting(false);
        }
    };

    const deleteVpn = async (cfg: IVpnConfig): Promise<void> => {
        if (!vpnConfigsUri) return;
        try {
            const res: { success?: boolean } = await del(
                `${vpnConfigsUri}/${cfg.aws_region_id}.json`,
                csrfToken,
            );
            if (res.success) {
                setVpnConfigs((prev) =>
                    prev.filter((c) => String(c.aws_region_id) !== String(cfg.aws_region_id)),
                );
            } else {
                alert(window.I18n.t('sandboxes.vpn_delete_error', 'Failed to delete VPN configuration'));
            }
        } catch (err) {
            alert(
                (err as Error).message ||
                    window.I18n.t('sandboxes.vpn_delete_error', 'Failed to delete VPN configuration'),
            );
        }
    };

    const downloadVpn = async (cfg: IVpnConfig): Promise<void> => {
        if (!vpnConfigsUri) return;
        try {
            const res: { success?: boolean; content?: string; filename?: string; error?: string } = await get(
                `${vpnConfigsUri}/${cfg.aws_region_id}/download.json`,
                csrfToken,
                {},
            );
            if (res.success && res.content != null) {
                const blob = new Blob([res.content], { type: 'application/x-openvpn-profile' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = res.filename || 'config.ovpn';
                a.click();
                URL.revokeObjectURL(url);
            } else {
                alert(
                    res.error ||
                        window.I18n.t('sandboxes.vpn_download_error', 'Failed to download VPN configuration'),
                );
            }
        } catch (err) {
            alert(
                (err as Error).message ||
                    window.I18n.t('sandboxes.vpn_download_error', 'Failed to download VPN configuration'),
            );
        }
    };

    const addSshKey = async (): Promise<void> => {
        if (!sshKeysUri || !sshKeyTitle.trim() || !sshKeyValue.trim()) return;
        const key = sshKeyValue.trim();
        const validPrefixes = ['ssh-rsa ', 'ssh-ed25519 ', 'ecdsa-sha2-'];
        if (!validPrefixes.some((p) => key.startsWith(p))) {
            alert(window.I18n.t('sandboxes.ssh_key_invalid_format', 'Key must start with ssh-rsa, ssh-ed25519, or ecdsa-sha2-'));
            return;
        }
        setSshKeySubmitting(true);
        try {
            const res: { success?: boolean; data?: ISshKey; error?: string } = await post(
                `${sshKeysUri}.json`,
                csrfToken,
                { title: sshKeyTitle.trim(), key },
            );
            if (res.success) {
                setSshKeyTitle('');
                setSshKeyValue('');
                setSshKeysAddOpen(false);
                // Re-fetch to ensure we have the correct structure from the API
                await fetchSshKeys();
            } else {
                alert(res.error || window.I18n.t('sandboxes.ssh_key_add_error', 'Failed to add SSH key'));
            }
        } catch (err) {
            alert((err as Error).message || window.I18n.t('sandboxes.ssh_key_add_error', 'Failed to add SSH key'));
        } finally {
            setSshKeySubmitting(false);
        }
    };

    const deleteSshKey = async (keyItem: ISshKey): Promise<void> => {
        if (!sshKeysUri) return;
        try {
            const res: { success?: boolean } = await del(
                `${sshKeysUri}/${keyItem.id}.json`,
                csrfToken,
            );
            if (res.success) {
                setSshKeys((prev) => (prev || []).filter((k) => k.id !== keyItem.id));
            } else {
                alert(window.I18n.t('sandboxes.ssh_key_delete_error', 'Failed to delete SSH key'));
            }
        } catch (err) {
            alert((err as Error).message || window.I18n.t('sandboxes.ssh_key_delete_error', 'Failed to delete SSH key'));
        }
    };

    const removeSandbox = (sandbox: ISandbox): void => {
        setSandboxes((oldSandboxesState: ISandbox[]) => {
            return oldSandboxesState.filter(
                (s: ISandbox) => s.id !== sandbox.id,
            );
        });
    };

    const onClickCreateNewSandbox = async (
        image: ISandboxImage,
    ): Promise<void> => {
        const newSandbox: ISandbox & { key: number } = {
            arn: '',
            auto_stop_in_mins: 0,
            aws_region: image.aws_region,
            destroy_uri: '',
            id: random(-1000, -1, false),
            image: image,
            key: random(1000, 1, false),
            renew_autostop_uri: '',
            ssh_uri: '',
            status: 'CREATING',
            task: '',
            tkn: '',
            url_ttyd: '',
            url_vscode: '',
            web: '',
            private_ip: '',
        };

        setSandboxes((oldSanboxes) => [...oldSanboxes, newSandbox]);

        try {
            const {
                data,
                success,
            }: { success: boolean; data: ISandbox } = await post(
                `${sandboxesUri}.json`,
                csrfToken,
                {
                    image_id: image.id,
                },
            );

            if (success === false) {
                alert(data);
                removeSandbox(newSandbox);
                return;
            }

            setSandboxes((oldSanboxes: ISandbox[]) =>
                oldSanboxes.map((s: ISandbox) =>
                    s.id !== newSandbox.id ? s : data,
                ),
            );
        } catch (err) {
            console.error(err);
            removeSandbox(newSandbox);
            alert(window.I18n.t('sandboxes.error_creating', 'Error while creating the sandbox. Try again later.'));
        }
    };

    const deleteSandbox = async (sandbox: ISandbox): Promise<void> => {
        const { data, success }: { data: string; success: boolean } = await del(
            sandbox.destroy_uri,
            csrfToken,
        );

        if (success === false) {
            throw new Error(data);
        }
        removeSandbox(sandbox);
    };

    if (loading || sandboxes === undefined) {
        return <div className="mt-2 alert alert-info">{window.I18n.t('sandboxes.loading', 'Loading...')}</div>;
    }

    const newSandboxPanel = (
        <div className="sandboxes-new-panel">
            <div className="sandboxes-new-header">
                <h3 className="sandboxes-new-title">
                    {window.I18n.t('sandboxes.new_sandbox', 'New sandbox')}
                </h3>
                <input
                    className="sandboxes-new-search"
                    onChange={(e): void => setImageSearch(e.target.value)}
                    placeholder="Filter by name..."
                    type="text"
                    value={imageSearch}
                />
            </div>
            <div className="sandboxes-new-list">
                {map(
                    filteredImagesByRegion,
                    (availableSandboxes: ISandboxImage[], regionName: string) => {
                        const isExpanded = expandedRegions.has(regionName);
                        return (
                            <div className="sandboxes-new-region" key={regionName}>
                                <button
                                    className="sandboxes-new-region-header"
                                    onClick={(): void => toggleRegion(regionName)}
                                    type="button"
                                >
                                    <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ marginRight: 10, flexShrink: 0 }} />
                                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{regionName}</span>
                                </button>
                                {isExpanded && (
                                    <div className="sandboxes-new-region-content">
                                        {map(availableSandboxes, (img: ISandboxImage, idx: number) => (
                                            <React.Fragment key={img.id}>
                                                {idx > 0 && <div className="sandboxes-new-image-separator" />}
                                                <a
                                                    className="sandboxes-new-image-item"
                                                    onClick={(): Promise<void> =>
                                                        onClickCreateNewSandbox(img)
                                                    }
                                                    role="button"
                                                >
                                                    {img.name}
                                                </a>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    },
                )}
                {Object.keys(filteredImagesByRegion).length === 0 && (
                    <div className="text-muted" style={{ padding: '20px', textAlign: 'center' }}>
                        No images match your search.
                    </div>
                )}
            </div>
        </div>
    );

    const sshKeysColumns = sshKeysUri && standalone && (
        <>
            <div className="sandboxes-ssh-keys-list-section">
                {sshKeysLoading ? (
                    <div className="sandboxes-empty-state">
                        <div className="sandboxes-empty-icon">
                            <i className="fa fa-spinner fa-spin" />
                        </div>
                        <div className="sandboxes-empty-title">
                            {window.I18n.t('sandboxes.loading', 'Loading...')}
                        </div>
                    </div>
                ) : (sshKeys || []).length === 0 ? (
                    <div className="sandboxes-empty-state">
                        <div className="sandboxes-empty-icon">
                            <i className="fa fa-key" />
                        </div>
                        <div className="sandboxes-empty-title">
                            {window.I18n.t('sandboxes.no_ssh_keys_yet', 'No SSH keys yet!')}
                        </div>
                        <p className="sandboxes-empty-text">
                            {window.I18n.t('sandboxes.no_ssh_keys_hint', 'Add an SSH key to connect to your sandboxes via SSH.')}
                        </p>
                    </div>
                ) : (
                    <div className="sandboxes-ssh-keys-list">
                        {(sshKeys || []).map((keyItem, idx) => (
                            <div className="sandbox-card" key={keyItem.id ?? `ssh-key-${idx}`}>
                                <div className="panel-body">
                                    <div className="sandbox-card-header">
                                        <div className="sandbox-card-title">
                                            <span className="sandbox-card-icon">
                                                <i className="fa fa-key" />
                                            </span>
                                            <span className="sandbox-card-name">{keyItem.title ?? (keyItem as Record<string, unknown>).name ?? keyItem.id}</span>
                                        </div>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={(): Promise<void> => deleteSshKey(keyItem)}
                                            title={window.I18n.t('sandboxes.delete_ssh_key', 'Delete SSH key')}
                                            type="button"
                                        >
                                            <i className="fa fa-trash" />
                                        </button>
                                    </div>
                                    <code className="sandboxes-ssh-key-item-key">{keyItem.key ?? (keyItem as Record<string, unknown>).public_key ?? ''}</code>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="sandboxes-ssh-keys-new-section">
                <button
                    aria-expanded={sshKeysAddOpen}
                    className="btn btn-lg btn-primary sandboxes-new-btn"
                    onClick={(): void => setSshKeysAddOpen(!sshKeysAddOpen)}
                    style={{ marginBottom: sshKeysAddOpen ? 16 : 0 }}
                    type="button"
                >
                    <i className="fa fa-key" />
                    {window.I18n.t('sandboxes.add_ssh_key', 'Add SSH key')}
                    <i className={`fa fa-chevron-${sshKeysAddOpen ? 'up' : 'down'}`} />
                </button>
                {sshKeysAddOpen && (
                    <div className="sandboxes-new-panel">
                        <div className="sandboxes-new-header">
                            <h3 className="sandboxes-new-title">
                                {window.I18n.t('sandboxes.add_ssh_key', 'Add SSH key')}
                            </h3>
                        </div>
                        <div className="sandboxes-new-list">
                            <div className="sandboxes-ssh-keys-add-form">
                                <input
                                    className="form-control sandboxes-new-search"
                                    onChange={(e): void => setSshKeyTitle(e.target.value)}
                                    placeholder={window.I18n.t('sandboxes.ssh_key_title_placeholder', 'Title (e.g. My laptop)')}
                                    type="text"
                                    value={sshKeyTitle}
                                />
                                <textarea
                                    className="form-control sandboxes-ssh-keys-textarea"
                                    onChange={(e): void => setSshKeyValue(e.target.value)}
                                    placeholder={window.I18n.t('sandboxes.ssh_key_placeholder', 'Paste your SSH public key (ssh-rsa, ssh-ed25519, or ecdsa-sha2-...)')}
                                    rows={3}
                                    value={sshKeyValue}
                                />
                                <div className="sandboxes-ssh-keys-add-actions">
                                    <button
                                        className="btn btn-primary"
                                        disabled={sshKeySubmitting}
                                        onClick={addSshKey}
                                        type="button"
                                    >
                                        {sshKeySubmitting ? window.I18n.t('sandboxes.adding', 'Adding...') : window.I18n.t('sandboxes.add_ssh_key', 'Add SSH key')}
                                    </button>
                                    <button
                                        className="btn btn-default"
                                        disabled={sshKeySubmitting}
                                        onClick={(): void => {
                                            setSshKeysAddOpen(false);
                                            setSshKeyTitle('');
                                            setSshKeyValue('');
                                        }}
                                        type="button"
                                    >
                                        {window.I18n.t('sandboxes.cancel', 'Cancel')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    const showVpnSection = Boolean(
        vpnConfigsUri && standalone && (vpnLoading || vpnRegions.length > 0),
    );

    const vpnColumns = showVpnSection && (
        <>
            <div className="sandboxes-vpn-list-section">
                {vpnLoading ? (
                    <div className="sandboxes-empty-state">
                        <div className="sandboxes-empty-icon">
                            <i className="fa fa-spinner fa-spin" />
                        </div>
                        <div className="sandboxes-empty-title">
                            {window.I18n.t('sandboxes.loading', 'Loading...')}
                        </div>
                    </div>
                ) : (vpnConfigs || []).length === 0 ? (
                    <div className="sandboxes-empty-state">
                        <div className="sandboxes-empty-icon">
                            <i className="fa fa-shield" />
                        </div>
                        <div className="sandboxes-empty-title">
                            {window.I18n.t('sandboxes.no_vpn_yet', 'No VPN configurations yet!')}
                        </div>
                        <p className="sandboxes-empty-text">
                            {window.I18n.t(
                                'sandboxes.no_vpn_hint',
                                'Create a VPN configuration per region to connect to COD networks.',
                            )}
                        </p>
                    </div>
                ) : (
                    <div className="sandboxes-vpn-list">
                        {(vpnConfigs || []).map((cfg, idx) => (
                            <div
                                className="sandbox-card"
                                key={String(cfg.aws_region_id ?? `vpn-${idx}`)}
                            >
                                <div className="panel-body">
                                    <div className="sandbox-card-header">
                                        <div className="sandbox-card-title">
                                            <span className="sandbox-card-icon">
                                                <i className="fa fa-shield" />
                                            </span>
                                            <span className="sandbox-card-name">{cfg.aws_region_full_name}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            <button
                                                className="btn btn-sm btn-default text-success"
                                                onClick={(): Promise<void> => downloadVpn(cfg)}
                                                title={window.I18n.t(
                                                    'sandboxes.download_vpn',
                                                    'Download configuration',
                                                )}
                                                type="button"
                                            >
                                                <i className="fa fa-download" />{' '}
                                                {window.I18n.t(
                                                    'sandboxes.download_vpn',
                                                    'Download configuration',
                                                )}
                                            </button>
                                            <button
                                                aria-label={window.I18n.t(
                                                    'sandboxes.delete_vpn',
                                                    'Delete VPN configuration',
                                                )}
                                                className="btn btn-sm btn-danger"
                                                onClick={(): Promise<void> => deleteVpn(cfg)}
                                                title={window.I18n.t(
                                                    'sandboxes.delete_vpn',
                                                    'Delete VPN configuration',
                                                )}
                                                type="button"
                                            >
                                                <i className="fa fa-trash" />
                                            </button>
                                        </div>
                                    </div>
                                    {(cfg.creation_date || cfg.expiration_date) && (
                                        <div
                                            className="text-muted"
                                            style={{
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                marginTop: 4,
                                            }}
                                        >
                                            {cfg.creation_date ? (
                                                <div>
                                                    {window.I18n.t(
                                                        'sandboxes.vpn_created_label',
                                                        'Created',
                                                    )}
                                                    : {cfg.creation_date}
                                                </div>
                                            ) : null}
                                            {cfg.expiration_date ? (
                                                <div>
                                                    {window.I18n.t(
                                                        'sandboxes.vpn_expires_label',
                                                        'Expires',
                                                    )}
                                                    : {cfg.expiration_date}
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="sandboxes-vpn-new-section">
                <button
                    aria-expanded={vpnAddOpen}
                    className="btn btn-lg btn-primary sandboxes-new-btn"
                    disabled={vpnRegionsWithoutConfig.length === 0}
                    onClick={(): void => setVpnAddOpen(!vpnAddOpen)}
                    style={{ marginBottom: vpnAddOpen ? 16 : 0 }}
                    type="button"
                >
                    <i className="fa fa-shield" />
                    {window.I18n.t('sandboxes.add_vpn', 'Create VPN configuration')}
                    <i className={`fa fa-chevron-${vpnAddOpen ? 'up' : 'down'}`} />
                </button>
                {vpnRegionsWithoutConfig.length === 0 && !vpnLoading && vpnRegions.length > 0 ? (
                    <p className="text-muted small" style={{ marginTop: 8 }}>
                        {window.I18n.t(
                            'sandboxes.vpn_all_regions',
                            'You already have a VPN configuration in every available region.',
                        )}
                    </p>
                ) : null}
                {vpnAddOpen && vpnRegionsWithoutConfig.length > 0 && (
                    <div className="sandboxes-new-panel">
                        <div className="sandboxes-new-header">
                            <h3 className="sandboxes-new-title">
                                {window.I18n.t('sandboxes.add_vpn', 'Create VPN configuration')}
                            </h3>
                        </div>
                        <div className="sandboxes-new-list">
                            <div className="sandboxes-vpn-create-form">
                                <label className="text-muted small" htmlFor="sandboxes-vpn-region">
                                    {window.I18n.t('sandboxes.vpn_region_label', 'Region')}
                                </label>
                                <select
                                    className="form-control sandboxes-new-search"
                                    id="sandboxes-vpn-region"
                                    onChange={(e): void => setVpnSelectedRegionId(e.target.value)}
                                    value={vpnSelectedRegionId}
                                >
                                    <option value="">
                                        {window.I18n.t(
                                            'sandboxes.vpn_select_region',
                                            'Choose a region',
                                        )}
                                    </option>
                                    {vpnRegionsWithoutConfig.map((r) => (
                                        <option key={String(r.aws_region_id)} value={String(r.aws_region_id)}>
                                            {r.aws_region_full_name}
                                        </option>
                                    ))}
                                </select>
                                <div className="sandboxes-vpn-create-actions">
                                    <button
                                        className="btn btn-primary"
                                        disabled={vpnSubmitting || !vpnSelectedRegionId}
                                        onClick={addVpn}
                                        type="button"
                                    >
                                        {vpnSubmitting
                                            ? window.I18n.t('sandboxes.vpn_creating', 'Creating...')
                                            : window.I18n.t('sandboxes.add_vpn', 'Create VPN configuration')}
                                    </button>
                                    <button
                                        className="btn btn-default"
                                        disabled={vpnSubmitting}
                                        onClick={(): void => {
                                            setVpnAddOpen(false);
                                            setVpnSelectedRegionId('');
                                        }}
                                        type="button"
                                    >
                                        {window.I18n.t('sandboxes.cancel', 'Cancel')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    if (standalone) {
        return (
            <>
                <div className="sandboxes-page">
                    <SandboxesPageSection
                        accent="containers"
                        iconClass="fa-terminal"
                        subtitle={window.I18n.t(
                            'sandboxes.section_containers_subtitle',
                            'Create and manage isolated Linux environments for your projects.',
                        )}
                        title={window.I18n.t('sandboxes.section_containers_heading', 'Sandboxes')}
                    >
                        <div className="sandboxes-list-section">
                            {sandboxes.length === 0 ? (
                                <div className="sandboxes-empty-state">
                                    <div className="sandboxes-empty-icon">
                                        <i className="fa fa-terminal" />
                                    </div>
                                    <div className="sandboxes-empty-title">
                                        {window.I18n.t('sandboxes.no_sandboxes_yet', 'No sandboxes yet!')}
                                    </div>
                                    <p className="sandboxes-empty-text">
                                        {window.I18n.t(
                                            'sandboxes.section_containers_empty_hint',
                                            'Create a new sandbox to get started. Choose an image from the panel on the right.',
                                        )}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    {sandboxes.map((sandbox) => (
                                        <Sandbox
                                            csrfToken={csrfToken}
                                            deleteSandbox={deleteSandbox}
                                            key={sandbox.id}
                                            sandboxesUri={sandboxesUri}
                                            {...sandbox}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="sandboxes-new-section">
                            <button
                                aria-expanded={newSandboxOpen}
                                className="btn btn-lg btn-primary sandboxes-new-btn"
                                disabled={sandboxes.length >= maxNumberOfContainers}
                                onClick={(): void => setNewSandboxOpen(!newSandboxOpen)}
                                style={{ marginBottom: newSandboxOpen ? 16 : 0 }}
                                type="button"
                            >
                                <i className="fa fa-plus" />
                                {window.I18n.t('sandboxes.new_sandbox', 'New sandbox')}
                                <i className={`fa fa-chevron-${newSandboxOpen ? 'up' : 'down'}`} />
                            </button>
                            {newSandboxOpen && newSandboxPanel}
                        </div>
                    </SandboxesPageSection>
                    {sshKeysColumns ? (
                        <SandboxesPageSection
                            accent="ssh"
                            iconClass="fa-key"
                            subtitle={window.I18n.t(
                                'sandboxes.section_ssh_subtitle',
                                'Add the public keys that are authorized on your sandboxes for SSH access.',
                            )}
                            title={window.I18n.t('sandboxes.my_ssh_keys', 'My SSH Keys')}
                        >
                            {sshKeysColumns}
                        </SandboxesPageSection>
                    ) : null}
                    {vpnColumns ? (
                        <SandboxesPageSection
                            accent="vpn"
                            iconClass="fa-shield"
                            subtitle={window.I18n.t(
                                'sandboxes.section_vpn_subtitle',
                                'One OpenVPN profile per region. Download and import it into your VPN client.',
                            )}
                            title={window.I18n.t('sandboxes.section_vpn_heading', 'VPN')}
                        >
                            {vpnColumns}
                        </SandboxesPageSection>
                    ) : null}
                </div>
            </>
        );
    }

    return (
        <div className="d-flex flex-column">
            <div className="dropdown pull-right" style={{ alignSelf: 'end' }}>
                <button
                    aria-expanded="false"
                    aria-haspopup="true"
                    className="btn btn-lg btn-primary dropdown-toggle"
                    data-toggle="dropdown"
                    disabled={sandboxes.length >= maxNumberOfContainers}
                    type="button"
                >
                    {window.I18n.t('sandboxes.new_sandbox', 'New sandbox')} <span className="caret" />
                </button>
                <ul className="dropdown-menu dropdown-menu-right" style={{ maxHeight: 400, overflowY: 'auto', minWidth: 280 }}>
                    <li style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                        <input
                            className="form-control"
                            onChange={(e): void => setImageSearch(e.target.value)}
                            onClick={(e): void => e.stopPropagation()}
                            placeholder="Filter..."
                            style={{ fontSize: 14 }}
                            type="text"
                            value={imageSearch}
                        />
                    </li>
                    {map(
                        filteredImagesByRegion,
                        (availableSandboxes: ISandboxImage[], regionName: string) => (
                            <React.Fragment key={regionName}>
                                <li className="divider" role="separator" />
                                <li className="dropdown-header">{regionName}</li>
                                {map(availableSandboxes, (img: ISandboxImage) => (
                                    <li key={img.id}>
                                        <a
                                            onClick={(): Promise<void> =>
                                                onClickCreateNewSandbox(img)
                                            }
                                            role="button"
                                        >
                                            {img.name}
                                        </a>
                                    </li>
                                ))}
                            </React.Fragment>
                        ),
                    )}
                </ul>
            </div>
            {sandboxes.length === 0 ? (
                <div className="mt-2 alert alert-info">
                    {window.I18n.t('sandboxes.no_sandboxes_yet', 'No sandboxes yet!')}
                </div>
            ) : (
                <div className="mt-2">
                    {sandboxes.map((sandbox) => (
                        <Sandbox
                            csrfToken={csrfToken}
                            deleteSandbox={deleteSandbox}
                            key={sandbox.id}
                            sandboxesUri={sandboxesUri}
                            {...sandbox}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
